import { ConstructionSiteList, GFAFullDetails } from "@dune/types";
import {
	formatCurrencyPrecise,
	formatDate,
	getActionTypesLabelNames,
	getBuildingTypeLabelName,
	getMOAName,
	getPrimeByProductType,
} from "@dune/utils";
import { ColumnDef } from "@tanstack/react-table";
import { ConstructionSiteStatusBadge } from "../../../component/constructionSites/ConstructionSiteStatusBadge";
const getBaseColumns = (
	hideBrokerColumn = false,
): ColumnDef<ConstructionSiteList | GFAFullDetails>[] => {
	const brokerColumn: ColumnDef<ConstructionSiteList | GFAFullDetails> = {
		accessorKey: "broker",
		header: "Apporteur",
		cell: ({ row }) => {
			const brokerSociety =
				"brokerSociety" in row.original ? row.original.brokerSociety : null;

			if (!brokerSociety) {
				return <span className="text-gray-400">Aucun</span>;
			}

			return (
				<div className="flex items-center gap-2">
					<div>
						<div className="font-medium">
							{brokerSociety.managingBrokerSociety?.denomination ||
								brokerSociety.denomination}
						</div>
					</div>
				</div>
			);
		},
		enableSorting: false,
	};

	return [
		{
			accessorKey: "id",
			header: "Réf",
			cell: ({ row }) => (
				<div className="font-medium">{`CH-${row.original.id.toString().padStart(5, "0")}`}</div>
			),
			enableSorting: true,
			sortingFn: (rowA, rowB) => {
				return rowA.original.id - rowB.original.id;
			},
		},
		{
			accessorKey: "client",
			header: "Maître d'ouvrage",
			cell: ({ row }) => (
				<div className="flex items-center gap-2">
					<div>
						<div className="font-medium">
							{getMOAName(row.original.constructionSiteClientDetails)}
						</div>
					</div>
				</div>
			),
			enableSorting: false,
		},
		...(hideBrokerColumn ? [] : [brokerColumn]),
		{
			accessorKey: "type",
			header: "Type de projet",
			cell: ({ row }) => {
				const details = row.original.constructionSiteDetails;
				if (!details || !details.constructionSiteAction) {
					return <span className="text-gray-400">/</span>;
				}

				const action = details.constructionSiteAction;
				return (
					action.types && (
						<div className="flex items-center gap-2">
							{getActionTypesLabelNames(action.types)}
						</div>
					)
				);
			},
			enableSorting: false,
		},
		{
			accessorKey: "project",
			header: "Type de chantier",
			cell: ({ row }) => {
				const details = row.original.constructionSiteDetails;
				if (!details || !details.constructionSiteAction) {
					return <span className="text-gray-400">/</span>;
				}

				const action = details.constructionSiteAction;
				return (
					<div className="flex items-center gap-2">
						{getBuildingTypeLabelName(action.buildingType || "")}
					</div>
				);
			},
			enableSorting: false,
		},
		{
			accessorKey: "amount",
			header: "Coût",
			cell: ({ row }) => {
				const amountTTC = row.original.constructionSiteDetails?.totalAmountTTC;
				const amountHT = row.original.constructionSiteDetails?.totalAmountHT;
				if (!amountTTC && !amountHT)
					return <span className="">Non renseigné</span>;

				return (
					<div className="font-medium">{`${formatCurrencyPrecise(amountHT || amountTTC)} ${amountHT ? "HT" : "TTC"}`}</div>
				);
			},
			enableSorting: false,
		},
		{
			accessorKey: "address",
			header: "Adresse",
			cell: ({ row }) => {
				const address = row.original.constructionSiteDetails?.address;
				if (!address)
					return <span className="text-gray-400">Non renseignée</span>;

				return (
					<div className="flex items-center gap-2">
						<div>
							<div className="font-medium">
								{address.streetNumber} {address.streetName}
							</div>
							<div className="text-sm text-gray-500">
								{address.postalCode} {address.city}
							</div>
						</div>
					</div>
				);
			},
			enableSorting: false,
		},
		{
			accessorKey: "status",
			header: "Statut",
			cell: ({ row }) => {
				const status = row.original.status;
				const isSkipped = row.original.isSkipped;
				return (
					<ConstructionSiteStatusBadge status={status} isSkipped={isSkipped} />
				);
			},
			enableSorting: false,
		},
		{
			accessorKey: "createdAt",
			header: "Créé",
			cell: ({ row }) => formatDate(row.original.createdAt),
			enableSorting: false,
		},
	];
};

// Colonnes de primes pour les chantiers de construction non syndic (DO, TRC - pas MRT, pas GFA)
const constructionSitePrimeColumns: ColumnDef<
	ConstructionSiteList | GFAFullDetails
>[] = [
	{
		accessorKey: "primeDO",
		header: "Prime DO",
		cell: ({ row }) => {
			const propositions =
				"propositions" in row.original ? row.original.propositions : [];
			const prime = getPrimeByProductType("DO", propositions);
			return prime ? (
				<div className="font-medium">{formatCurrencyPrecise(prime)}</div>
			) : (
				<span className="text-gray-400">/</span>
			);
		},
		enableSorting: false,
	},
	{
		accessorKey: "primeTRC",
		header: "Prime TRC",
		cell: ({ row }) => {
			const propositions =
				"propositions" in row.original ? row.original.propositions : [];
			const prime = getPrimeByProductType("TRC", propositions);
			return prime ? (
				<div className="font-medium">{formatCurrencyPrecise(prime)}</div>
			) : (
				<span className="text-gray-400">/</span>
			);
		},
		enableSorting: false,
	},
];
// Colonnes de primes pour les chantiers de construction syndic (MRT)
const constructionSiteSyndicPrimeColumns: ColumnDef<
	ConstructionSiteList | GFAFullDetails
>[] = [
	{
		accessorKey: "primeMRT",
		header: "Prime MRT",
		cell: ({ row }) => {
			const propositions =
				"propositions" in row.original ? row.original.propositions : [];
			const prime = getPrimeByProductType("MRT", propositions);
			return prime ? (
				<div className="font-medium">{formatCurrencyPrecise(prime)}</div>
			) : (
				<span className="text-gray-400">/</span>
			);
		},
		enableSorting: false,
	},
];

// Colonnes de primes pour GFA (seulement GFA)
const gfaPrimeColumns: ColumnDef<ConstructionSiteList | GFAFullDetails>[] = [
	{
		accessorKey: "primeGFA",
		header: "Prime GFA",
		cell: ({ row }) => {
			const propositions =
				"propositions" in row.original ? row.original.propositions : [];
			const prime = getPrimeByProductType("GFA", propositions);
			return prime ? (
				<div className="font-medium">{formatCurrencyPrecise(prime)}</div>
			) : (
				<span className="text-gray-400">/</span>
			);
		},
		enableSorting: false,
	},
];

// Export des colonnes selon le type
export const getConstructionSitesColumns = (
	hideBrokerColumn = false,
): ColumnDef<ConstructionSiteList | GFAFullDetails>[] => [
	...getBaseColumns(hideBrokerColumn),
	...constructionSitePrimeColumns,
];

export const getGfaColumns = (
	hideBrokerColumn = false,
): ColumnDef<ConstructionSiteList | GFAFullDetails>[] => [
	...getBaseColumns(hideBrokerColumn),
	...gfaPrimeColumns,
];

export const getConstructionSiteSyndicColumns = (
	hideBrokerColumn = false,
): ColumnDef<ConstructionSiteList | GFAFullDetails>[] => [
	...getBaseColumns(hideBrokerColumn),
	...constructionSiteSyndicPrimeColumns,
];

export const getDuneConstructionSiteColumns = (
	hideBrokerColumn = false,
): ColumnDef<ConstructionSiteList | GFAFullDetails>[] => [
	...getBaseColumns(hideBrokerColumn),
	...constructionSitePrimeColumns,
	...constructionSiteSyndicPrimeColumns,
];

// Legacy exports for backward compatibility (default: show broker column)
export const constructionSitesColumns = getConstructionSitesColumns();
export const gfaColumns = getGfaColumns();
export const constructionSiteSyndicColumns = getConstructionSiteSyndicColumns();
export const duneConstructionSiteColumns = getDuneConstructionSiteColumns();
